
import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Dialog,
  Typography,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";

import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import FingerprintRoundedIcon from "@mui/icons-material/FingerprintRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";

import { Html5Qrcode } from "html5-qrcode";

const API_BASE =
  "https://api.iskconsolapur.org/api/ICC";

const QRScanner = () => {
  const scannerRef = useRef(null);
  const processingRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || result) return;

    const timer = setTimeout(() => {
      startScanner();
    }, 350);

    return () => clearTimeout(timer);
  }, [open, result]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  /* -------------------------------------------------------
     QR ID extraction
  ------------------------------------------------------- */

  const extractQrId = (value) => {
    if (!value) {
      throw new Error("Invalid QR");
    }

    const trimmed = value.trim();

    try {
      const url = new URL(trimmed);

      const parts = url.pathname
        .split("/")
        .filter(Boolean);

      const verifyIndex = parts.findIndex(
        (part) =>
          part.toLowerCase() === "verify-qrcode"
      );

      if (
        verifyIndex !== -1 &&
        parts[verifyIndex + 1]
      ) {
        return parts[verifyIndex + 1];
      }

      if (parts.length) {
        return parts[parts.length - 1];
      }
    } catch {
      // Continue with fallback
    }

    /*
     * Backward compatibility.
     */
    try {
      const decoded = atob(trimmed);

      if (decoded) {
        return decoded;
      }
    } catch {
      // Not base64
    }

    return trimmed;
  };

  /* -------------------------------------------------------
     Scanner
  ------------------------------------------------------- */

  const startScanner = async () => {
    try {
      if (scannerRef.current) return;

      processingRef.current = false;

      const cameras =
        await Html5Qrcode.getCameras();

      if (!cameras?.length) {
        setResult({
          eligible: false,
          title: "Camera Unavailable",
          message:
            "No camera was found on this device.",
          data: null,
        });

        return;
      }

      const scanner = new Html5Qrcode(
        "qr-reader"
      );

      scannerRef.current = scanner;

      await scanner.start(
        {
          facingMode: {
            exact: "environment",
          },
        },
        {
          fps: 12,
          qrbox: {
            width: 245,
            height: 245,
          },
          aspectRatio: 1,
        },
        async (decodedText) => {
          if (processingRef.current) {
            return;
          }

          processingRef.current = true;

          await stopScanner();

          await handleQR(decodedText);
        },
        () => {}
      );
    } catch (error) {
      console.error(error);

      setResult({
        eligible: false,
        title: "Camera Error",
        message:
          error?.message ||
          "Unable to access the camera.",
        data: null,
      });
    }
  };

  const stopScanner = async () => {
    try {
      const scanner = scannerRef.current;

      if (!scanner) return;

      scannerRef.current = null;

      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch {}

      try {
        await scanner.clear();
      } catch {}
    } catch {}
  };

  /* -------------------------------------------------------
     API
  ------------------------------------------------------- */

  const handleQR = async (value) => {
    try {
      setLoading(true);
      setResult(null);

      const qrId = extractQrId(value);

      console.log("QR ID:", qrId);

      const response = await fetch(
        `${API_BASE}/update-payment-quotation-status/${qrId}`,
        {
          method: "GET",
        }
      );

      let responseData = null;

      try {
        responseData =
          await response.json();
      } catch {}

      console.log(
        "API RESPONSE:",
        responseData
      );

      /*
       * SUCCESS
       */
      if (
        response.ok &&
        responseData?.status !== "failed"
      ) {
        setResult({
          eligible: true,
          title: "ELIGIBLE",
          message:
            responseData?.message ||
            "Seva verified successfully.",
          data:
            responseData?.data ||
            responseData,
        });

        return;
      }

      /*
       * ALREADY USED
       */
      if (
        responseData?.message
          ?.toLowerCase()
          .includes("status already true")
      ) {
        setResult({
          eligible: false,
          title: "NOT ELIGIBLE",
          message:
            "This seva has already been used.",
          data:
            responseData?.data || null,
        });

        return;
      }

      /*
       * GENERAL FAILURE
       */
      setResult({
        eligible: false,
        title: "NOT ELIGIBLE",
        message:
          responseData?.message ||
          "This QR code could not be verified.",
        data:
          responseData?.data || null,
      });
    } catch (error) {
      console.error(error);

      setResult({
        eligible: false,
        title: "INVALID QR",
        message:
          "This QR code is invalid or could not be verified.",
        data: null,
      });
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     Actions
  ------------------------------------------------------- */

  const openScanner = () => {
    setResult(null);
    setOpen(true);
    processingRef.current = false;
  };

  const closeScanner = async () => {
    await stopScanner();

    setOpen(false);
    setResult(null);
    setLoading(false);

    processingRef.current = false;
  };

  const scanAgain = async () => {
    await stopScanner();

    setResult(null);
    setLoading(false);

    processingRef.current = false;

    setTimeout(() => {
      startScanner();
    }, 250);
  };

  /* -------------------------------------------------------
     Formatting
  ------------------------------------------------------- */

  const formatDate = (value) => {
    if (!value) return "-";

    try {
      return new Date(value).toLocaleString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    } catch {
      return value;
    }
  };

  const shortId = (
    value,
    start = 7,
    end = 5
  ) => {
    if (!value) return "-";

    if (value.length <= start + end + 3) {
      return value;
    }

    return `${value.slice(
      0,
      start
    )}...${value.slice(-end)}`;
  };

  /* -------------------------------------------------------
     Compact Detail Item
  ------------------------------------------------------- */

  const Detail = ({
    icon,
    label,
    value,
    large = false,
  }) => {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return null;
    }

    return (
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "10px",
            flexShrink: 0,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            background:
              "rgba(99,102,241,0.08)",

            color: "#6366f1",
          }}
        >
          {icon}
        </Box>

        <Box minWidth={0}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.7,
              textTransform: "uppercase",
              color: "#94a3b8",
              lineHeight: 1.1,
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              fontSize: large ? 15 : 11,
              fontWeight: 800,
              color: "#1e293b",
              lineHeight: 1.25,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    );
  };

  /* -------------------------------------------------------
     Result Screen
  ------------------------------------------------------- */

  const ResultScreen = () => {
    const data = result?.data || {};
    const item = data?.item || {};

    const success = result?.eligible;

    return (
      <Box
        sx={{
          height: "100dvh",
          width: "100%",
          overflow: "hidden",

          background: success
            ? "linear-gradient(160deg,#ecfdf5 0%,#ffffff 38%,#f8fafc 100%)"
            : "linear-gradient(160deg,#fef2f2 0%,#ffffff 38%,#f8fafc 100%)",

          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ----------------------------------------------
            TOP BAR
        ---------------------------------------------- */}

        <Box
          sx={{
            height: 54,
            flexShrink: 0,

            px: 1.5,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            background:
              "rgba(255,255,255,0.82)",

            backdropFilter: "blur(14px)",

            borderBottom:
              "1px solid rgba(15,23,42,0.06)",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 15,
                color: "#0f172a",
              }}
            >
              Seva Verification
            </Typography>

            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: "#94a3b8",
                letterSpacing: 0.5,
              }}
            >
              ISKCON - ICC SEVA DESK
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={closeScanner}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* ----------------------------------------------
            STATUS HERO
        ---------------------------------------------- */}

        <Box
          sx={{
            px: 1.5,
            pt: 1.5,
            pb: 1,

            flexShrink: 0,

            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 62,
              height: 62,
              borderRadius: "20px",
              flexShrink: 0,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              background: success
                ? "#dcfce7"
                : "#fee2e2",

              boxShadow: success
                ? "0 8px 25px rgba(22,163,74,.15)"
                : "0 8px 25px rgba(220,38,38,.15)",
            }}
          >
            {success ? (
              <CheckCircleRoundedIcon
                sx={{
                  fontSize: 47,
                  color: "#16a34a",
                }}
              />
            ) : (
              <CancelRoundedIcon
                sx={{
                  fontSize: 47,
                  color: "#dc2626",
                }}
              />
            )}
          </Box>

          <Box flex={1} minWidth={0}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
            >
              <Typography
                sx={{
                  fontSize: 26,
                  lineHeight: 1,
                  fontWeight: 950,

                  color: success
                    ? "#15803d"
                    : "#b91c1c",
                }}
              >
                {result.title}
              </Typography>

              <Chip
                size="small"
                label={
                  success
                    ? "ACCEPT"
                    : "REJECT"
                }
                sx={{
                  height: 22,
                  fontSize: 9,
                  fontWeight: 900,

                  background:
                    success
                      ? "#16a34a"
                      : "#dc2626",

                  color: "white",
                }}
              />
            </Box>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 11,
                lineHeight: 1.25,
                color: "#64748b",
                fontWeight: 600,
              }}
            >
              {result.message}
            </Typography>
          </Box>
        </Box>

        {/* ----------------------------------------------
            MAIN CONTENT
        ---------------------------------------------- */}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,

            px: 1.5,
            pb: 1,

            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* SEVA CARD */}
          <Box
            sx={{
              borderRadius: 3,
              p: 1.5,

              background:
                "rgba(255,255,255,0.92)",

              border:
                "1px solid rgba(15,23,42,0.07)",

              boxShadow:
                "0 5px 20px rgba(15,23,42,0.05)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.8}
              >
                <VolunteerActivismRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: "#7c3aed",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: 0.7,
                    textTransform: "uppercase",
                    color: "#475569",
                  }}
                >
                  Seva Details
                </Typography>
              </Box>

              {item.total_price !==
                undefined && (
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 950,
                    color: "#7c3aed",
                  }}
                >
                  ₹{item.total_price}
                </Typography>
              )}
            </Box>

            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.1,
              }}
            >
              {item.category_name ||
                "Seva"}
            </Typography>

            {item.description && (
              <Typography
                sx={{
                  fontSize: 10,
                  color: "#64748b",
                  mt: 0.3,
                }}
              >
                {item.description}
              </Typography>
            )}

            <Box
              sx={{
                mt: 1.2,

                display: "grid",
                gridTemplateColumns:
                  "1fr 1fr 1fr",

                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 700,
                  }}
                >
                  QUANTITY
                </Typography>

                <Typography
                  fontWeight={900}
                  fontSize={12}
                >
                  {item.quantity ?? "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 700,
                  }}
                >
                  UNIT PRICE
                </Typography>

                <Typography
                  fontWeight={900}
                  fontSize={12}
                >
                  ₹{item.unit_price ?? "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 700,
                  }}
                >
                  TOTAL
                </Typography>

                <Typography
                  fontWeight={900}
                  fontSize={12}
                >
                  ₹{item.total_price ?? "-"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* DEVOTEE + PAYMENT */}
          <Box
            sx={{
              borderRadius: 3,
              p: 1.5,

              background:
                "rgba(255,255,255,0.92)",

              border:
                "1px solid rgba(15,23,42,0.07)",

              boxShadow:
                "0 5px 20px rgba(15,23,42,0.05)",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.7,
                textTransform: "uppercase",
                color: "#475569",
                mb: 1.2,
              }}
            >
              Devotee & Payment
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 1.5,
              }}
            >
              <Detail
                icon={
                  <BadgeRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Sevak ID"
                value={data.sevakId}
              />

              <Detail
                icon={
                  <PersonRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Identifier"
                value={data.identifierId}
              />

              <Detail
                icon={
                  <PaymentsRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Payment ID"
                value={shortId(
                  data.paymentId
                )}
              />

              <Detail
                icon={
                  <ReceiptLongRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Quote ID"
                value={shortId(
                  data.quote_id
                )}
              />
            </Box>
          </Box>

          {/* QR + TIME */}
          <Box
            sx={{
              borderRadius: 3,
              p: 1.5,

              background:
                "rgba(255,255,255,0.92)",

              border:
                "1px solid rgba(15,23,42,0.07)",

              boxShadow:
                "0 5px 20px rgba(15,23,42,0.05)",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.7,
                textTransform: "uppercase",
                color: "#475569",
                mb: 1.2,
              }}
            >
              QR Verification
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 1.5,
              }}
            >
              <Detail
                icon={
                  <FingerprintRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="QR ID"
                value={shortId(
                  data.qrQode_id
                )}
              />

              <Detail
                icon={
                  <AccessTimeRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Generated"
                value={formatDate(
                  data.generatedAt
                )}
              />

              <Detail
                icon={
                  <AccessTimeRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Updated"
                value={formatDate(
                  data.updatedAt
                )}
              />

              <Detail
                icon={
                  <FingerprintRoundedIcon
                    sx={{ fontSize: 17 }}
                  />
                }
                label="Status"
                value={
                  data.status === true
                    ? "Already Used"
                    : "Verified"
                }
              />
            </Box>
          </Box>

          {/* WARNING / SUCCESS MESSAGE */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,

              borderRadius: 3,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              px: 2,

              background: success
                ? "rgba(22,163,74,0.07)"
                : "rgba(220,38,38,0.07)",

              border: success
                ? "1px solid rgba(22,163,74,0.15)"
                : "1px solid rgba(220,38,38,0.15)",
            }}
          >
            <Typography
              textAlign="center"
              sx={{
                fontSize: 12,
                fontWeight: 800,

                color: success
                  ? "#166534"
                  : "#991b1b",
              }}
            >
              {success
                ? "✓ This seva is verified and can be accepted."
                : "✕ The QR code has already been processed or is invalid."}
            </Typography>
          </Box>
        </Box>

        {/* ----------------------------------------------
            FIXED BOTTOM ACTIONS
        ---------------------------------------------- */}

        <Box
          sx={{
            flexShrink: 0,

            p: 1.5,

            display: "grid",
            gridTemplateColumns:
              "1fr 1fr",

            gap: 1,

            background:
              "rgba(255,255,255,0.95)",

            backdropFilter: "blur(14px)",

            borderTop:
              "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <Button
            variant="outlined"
            onClick={closeScanner}
            sx={{
              height: 48,
              borderRadius: 2.5,
              fontWeight: 900,
              fontSize: 12,
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            startIcon={
              <RestartAltRoundedIcon />
            }
            onClick={scanAgain}
            sx={{
              height: 48,
              borderRadius: 2.5,
              fontWeight: 900,
              fontSize: 12,

              background:
                "linear-gradient(135deg,#7c3aed,#db2777)",

              boxShadow:
                "0 7px 20px rgba(124,58,237,.25)",
            }}
          >
            Scan Another
          </Button>
        </Box>
      </Box>
    );
  };

  /* -------------------------------------------------------
     Scanner Screen
  ------------------------------------------------------- */

  const ScannerScreen = () => (
    <Box
      sx={{
        height: "100dvh",
        background: "#020617",
        color: "white",

        display: "flex",
        flexDirection: "column",

        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 62,
          flexShrink: 0,

          px: 1.5,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          background:
            "rgba(2,6,23,.85)",

          backdropFilter: "blur(15px)",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 900,
            }}
          >
            Scan Seva QR
          </Typography>

          <Typography
            sx={{
              fontSize: 10,
              color: "#94a3b8",
              fontWeight: 600,
            }}
          >
            Point camera at devotee QR
          </Typography>
        </Box>

        <IconButton
          onClick={closeScanner}
          sx={{
            color: "white",
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* Camera */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,

          position: "relative",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          overflow: "hidden",
        }}
      >
        <Box
          id="qr-reader"
          sx={{
            width: "100%",
            height: "100%",

            "& video": {
              width: "100% !important",
              height: "100% !important",
              objectFit: "cover",
            },

            "& img": {
              display: "none",
            },

            "& button": {
              display: "none",
            },

            "& select": {
              display: "none",
            },
          }}
        />

        {/* Camera dark overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,

            pointerEvents: "none",

            background:
              "linear-gradient(to bottom,rgba(2,6,23,.55),transparent 25%,transparent 75%,rgba(2,6,23,.75))",
          }}
        />

        {/* Scanner frame */}
        <Box
          sx={{
            position: "absolute",

            width: 255,
            height: 255,

            borderRadius: 5,

            border:
              "2px solid rgba(255,255,255,.9)",

            boxShadow:
              "0 0 0 9999px rgba(0,0,0,.30)",

            pointerEvents: "none",
          }}
        >
          {/* Corners */}
          <Box
            sx={{
              position: "absolute",
              inset: -2,

              borderRadius: 5,

              border:
                "3px solid transparent",

              borderTopColor:
                "#ec4899",

              borderLeftColor:
                "#ec4899",
            }}
          />

          {/* Scan line */}
          <Box
            sx={{
              position: "absolute",

              left: 12,
              right: 12,

              height: 2,

              top: "50%",

              background:
                "linear-gradient(90deg,transparent,#ec4899,transparent)",

              boxShadow:
                "0 0 15px #ec4899",

              animation:
                "qrScan 2s ease-in-out infinite",

              "@keyframes qrScan": {
                "0%": {
                  transform:
                    "translateY(-100px)",
                },

                "50%": {
                  transform:
                    "translateY(100px)",
                },

                "100%": {
                  transform:
                    "translateY(-100px)",
                },
              },
            }}
          />
        </Box>

        {/* Instruction */}
        <Box
          sx={{
            position: "absolute",
            bottom: 28,
            left: 20,
            right: 20,

            textAlign: "center",

            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            Align QR inside the frame
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: "#cbd5e1",
              mt: 0.5,
            }}
          >
            Scanning automatically...
          </Typography>
        </Box>
      </Box>

      {/* Bottom */}
      <Box
        sx={{
          height: 75,
          flexShrink: 0,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          background:
            "rgba(2,6,23,.92)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,

            px: 2,
            py: 1,

            borderRadius: 99,

            background:
              "rgba(255,255,255,.08)",
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#22c55e",
              boxShadow:
                "0 0 10px #22c55e",
            }}
          />

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#cbd5e1",
            }}
          >
            Camera ready
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Floating scan button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 18,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
        }}
      >
        <Button
          onClick={openScanner}
          variant="contained"
          startIcon={
            <QrCodeScannerIcon />
          }
          sx={{
            minWidth: 120,
            height: 58,

            borderRadius: 99,

            fontSize: 14,
            fontWeight: 900,

            background:
              "linear-gradient(135deg,#7c3aed,#db2777)",

            boxShadow:
              "0 12px 35px rgba(124,58,237,.35)",
          }}
        >
          SCAN
        </Button>
      </Box>

      {/* Fullscreen scanner */}
      <Dialog
        open={open}
        onClose={closeScanner}
        fullScreen
        PaperProps={{
          sx: {
            margin: 0,
            borderRadius: 0,
            overflow: "hidden",
            background: "#020617",
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              height: "100dvh",

              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",

              background:
                "linear-gradient(160deg,#0f172a,#020617)",

              color: "white",
            }}
          >
            <CircularProgress
              size={58}
              thickness={4}
              sx={{
                color: "#a855f7",
              }}
            />

            <Typography
              sx={{
                mt: 3,
                fontSize: 20,
                fontWeight: 900,
              }}
            >
              Verifying QR...
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 12,
                color: "#94a3b8",
              }}
            >
              Checking seva eligibility
            </Typography>
          </Box>
        ) : result ? (
          <ResultScreen />
        ) : (
          <ScannerScreen />
        )}
      </Dialog>
    </>
  );
};

export default QRScanner;